import React, { useState } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import BuildIcon from '@mui/icons-material/Build';
import { Link } from 'react-router-dom';
import MobileRestriction from '../../../components/MobileRestriction';
import { BACKEND_API_URL } from "../../../utils/api";

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const MoveToRootPage = () => {
    const [itemId, setItemId] = useState('');
    const [moving, setMoving] = useState(false);
    const [lastMoved, setLastMoved] = useState(null);

    const handleMoveToRoot = async () => {
        if (!itemId || !itemId.trim()) {
            toast.error('Please enter an item ID');
            return;
        }

        const id = parseInt(itemId);
        if (isNaN(id)) {
            toast.error('Please enter a valid numeric ID');
            return;
        }

        setMoving(true);
        try {
            const accessToken = Cookies.get('access_token');
            const payload = {
                new_parent_id: null // Set parent to null (root)
            };

            const response = await axios.post(
                `${REACT_APP_BACKEND_API_URL}/api/debug/fix_circular_dependency/${id}/`,
                payload,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.success) {
                toast.success(`✅ Successfully moved item ${id} to root!`);
                setLastMoved({
                    id: response.data.item.id,
                    name: response.data.item.name,
                    previousParent: response.data.item.parent_name
                });
                setItemId('');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Error moving item to root';
            toast.error(errorMsg);
            console.error('Error:', error);
        } finally {
            setMoving(false);
        }
    };

    // Mobile portrait mode check
    const mobilePortrait = window?.innerWidth <= 600;
    if (mobilePortrait) {
        return <MobileRestriction />;
    }

    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <BuildIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                        Move Item to Root
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Enter an item ID to move it to the root level (remove its parent)
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 4 }}>
                    <Typography variant="body2">
                        <strong>What does this do?</strong>
                        <br />
                        This tool moves any item to the root level by removing its parent relationship.
                        Useful for:
                    </Typography>
                    <ul style={{ marginTop: 8, marginBottom: 0 }}>
                        <li>Breaking circular dependencies</li>
                        <li>Reorganizing your hierarchy</li>
                        <li>Fixing orphaned items</li>
                    </ul>
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                        label="Item ID"
                        type="number"
                        value={itemId}
                        onChange={(e) => setItemId(e.target.value)}
                        placeholder="e.g., 3174"
                        variant="outlined"
                        fullWidth
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleMoveToRoot();
                            }
                        }}
                        helperText="Enter the numeric ID of the item you want to move to root"
                    />

                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        fullWidth
                        startIcon={moving ? <CircularProgress size={20} color="inherit" /> : <BuildIcon />}
                        onClick={handleMoveToRoot}
                        disabled={moving || !itemId}
                        sx={{ py: 1.5 }}
                    >
                        {moving ? 'Moving to Root...' : 'Move to Root'}
                    </Button>

                    {lastMoved && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                <strong>Last Operation:</strong>
                                <br />
                                Item <strong>{lastMoved.name}</strong> (ID: {lastMoved.id}) 
                                {lastMoved.previousParent && ` was moved from parent "${lastMoved.previousParent}"`} to root level.
                            </Typography>
                        </Alert>
                    )}
                </Box>

                <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">
                        💡 <strong>Tip:</strong> After moving items, you can check the{' '}
                        <a href="/debug" style={{ color: 'inherit', textDecoration: 'underline' }}>
                            Debug page
                        </a>
                        {' '}to verify circular dependencies are resolved.
                    </Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default MoveToRootPage;

